import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const AiChatScreenContext = createContext({
	chatIconVisible: false,
	setChatIconVisible: () => {},
	appSidebarExpanded: false,
	appSidebarWidth: 60,
	setAppSidebarLayout: () => {},
});

/**
 * Controls when the header chatbot orb is shown.
 * Monitor Today / TnT Linkage pages register themselves while mounted.
 * Also tracks the main app left sidebar expand width for expanded-chat layout.
 */
export const AiChatScreenProvider = ({ children }) => {
	const [chatIconVisible, setChatIconVisibleState] = useState(false);
	const [appSidebarExpanded, setAppSidebarExpanded] = useState(false);
	const [appSidebarWidth, setAppSidebarWidth] = useState(60);

	const setChatIconVisible = useCallback((visible) => {
		setChatIconVisibleState(Boolean(visible));
	}, []);

	const setAppSidebarLayout = useCallback(({ expanded, width } = {}) => {
		if (typeof expanded === "boolean") {
			setAppSidebarExpanded(expanded);
		}
		if (typeof width === "number" && Number.isFinite(width) && width > 0) {
			setAppSidebarWidth(width);
		}
	}, []);

	const value = useMemo(
		() => ({
			chatIconVisible,
			setChatIconVisible,
			appSidebarExpanded,
			appSidebarWidth,
			setAppSidebarLayout,
		}),
		[
			chatIconVisible,
			setChatIconVisible,
			appSidebarExpanded,
			appSidebarWidth,
			setAppSidebarLayout,
		]
	);

	return (
		<AiChatScreenContext.Provider value={value}>
			{children}
		</AiChatScreenContext.Provider>
	);
};

AiChatScreenProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useAiChatScreen = () => useContext(AiChatScreenContext);

/**
 * Call from screens that should expose the chatbot icon while mounted.
 */
export function useRegisterAiChatScreen(enabled = true) {
	const { setChatIconVisible } = useAiChatScreen();

	useEffect(() => {
		if (!enabled) return undefined;
		setChatIconVisible(true);
		return () => setChatIconVisible(false);
	}, [enabled, setChatIconVisible]);
}
